/**
 * ATL-1955 — the date a supply order is SAVED with is its own, never one made up on the way.
 *
 * SupplyOrderDetailScreen seeded its date as `detail?.date?.toDate?.() || new Date()` and
 * `handleSave` writes that state into the Firestore order (the ATL-1041 SUPPLY_ORDER write-back).
 * For a new order "now" is the right default. For an EXISTING order it replaced a date the screen
 * could not read with today's: a Supabase-read order carries no `date` at all (ATL-1962), and an
 * order re-opened from the list after a save holds the plain `Date` the save put there (no
 * `toDate`). The date field is disabled for an existing order, so the merchant never chose that
 * value — the save simply overwrote the real one in the master copy.
 *
 * {@link readSupplyOrderDate} reads both shapes the order date legitimately arrives in; an existing
 * order whose date it cannot read seeds `null` ({@link initialSupplyOrderDate}), and the save refuses.
 */

/**
 * The order date as a `Date`: a Firestore Timestamp (`toDate()`) or a valid `Date`; otherwise null.
 *
 * @param {unknown} raw
 * @returns {Date | null}
 */
export const readSupplyOrderDate = (raw) => {
  const date = typeof raw?.toDate === 'function' ? raw.toDate() : raw
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null
}

/**
 * The date the detail screen starts from: the order's own, or now for a new order (no `detail`).
 * An existing order whose date cannot be read starts from null, never from now.
 *
 * @param {{date?: unknown} | null | undefined} detail
 * @returns {Date | null}
 */
export const initialSupplyOrderDate = (detail) => (detail ? readSupplyOrderDate(detail.date) : new Date())
